/* Presentations step 2 — her locked My Voice & Branding, read as a deck skin.
 *
 * THIS IS THE ACCELERATOR-TO-MOMENTUM SEAM (CLAUDE.md): it reads prose Brill
 * wrote, and it fails silently if it reads wrong — a deck in the wrong colours
 * looks finished. So it never guesses quietly: every role it could not find,
 * and every role it inferred rather than read, goes into `notes`, and a role
 * it cannot place is simply absent (resolveSkin then fills it and says so).
 *
 * TWO SHAPES OF PROSE, both real:
 *   · the current prompt (api/_lib/prompts.js, Brand Feeling & Color Palette):
 *       - Primary: #XXXXXX — page base or dominant color …
 *       - Accent 1: #XXXXXX — CTA buttons …
 *     where "Primary" means the PAGE BASE, often a cream — not the brand colour;
 *   · earlier locks, written freely:
 *       - **Warm Cream** — #F7F5F2 — primary light base
 *       - **Medium Teal** — #007D6E — primary brand color
 *   So roles are read from the WORDS beside each colour, then from its label,
 *   then from its lightness — in that order.
 *
 * Fonts come from Typography Pairing, where Brill wraps every font name in
 * {{Font Name}} (a live preview in the chat): the one under a Headings label
 * is the heading font, the one under Body is the body font.
 */

import { luminance } from "./deckLayout";
import { namedPaletteEntries } from "./colourNames";

const HEX_LINE = /#([0-9a-f]{6}|[0-9a-f]{3})\b/i;
const strip = (s) => String(s || "").replace(/[*_`]/g, "").replace(/\s+/g, " ").trim();

function norm(h) {
  const s = h.toUpperCase();
  return s.length === 3 ? s.split("").map((c) => c + c).join("") : s;
}

function saturation(h) {
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  return max === 0 ? 0 : (max - min) / max;
}

/** Every colour in the prose, once, with the words around it.
 *  The first mention wins: later lines (button rules, accessibility notes)
 *  repeat hexes in passing. A SECOND PALETTE — the prompt offers five — stops
 *  the read at the point a role label repeats, and says so. */
export function paletteEntries(text) {
  const out = [];
  const seenLabels = new Set();
  let cut = false;
  for (const raw of String(text || "").split(/\r?\n/)) {
    const m = HEX_LINE.exec(raw);
    if (!m) continue;
    const hex = norm(m[1]);
    const before = strip(raw.slice(0, m.index)).replace(/^[-•\s]+/, "").replace(/[\s:—–(-]+$/, "");
    const after = strip(raw.slice(m.index + m[0].length)).replace(/^[\s)—–:-]+/, "");
    const label = (before.match(/^(primary|secondary|tertiary|accent ?1|accent ?2)\b/i)?.[1] || "").toLowerCase().replace(/\s/g, "");
    if (label) {
      if (seenLabels.has(label)) { cut = true; break; }
      seenLabels.add(label);
    }
    if (out.some((e) => e.hex === hex)) continue;
    out.push({ hex, name: before, words: `${before} ${after}`.toLowerCase(), label });
  }
  if (out.length) return { entries: out, cut };
  return { entries: namedEntries(text), cut: false, fromNames: true };
}

/* NO CODES AT ALL — a palette written as names (her ask, 2026-10-27, from
   Jenn's: "Primary — Deep Dusty Blue: … used for … headers"). Each named
   colour becomes a code from its name (colourNames.js), and keeps its words
   so the role rules below place it exactly as they would a coded one. A
   TEXT colour's words are replaced by what its lightness says it is for: her
   prose calls dark text "deep" and "dark", which the rules would otherwise
   read as text for a DARK background. */
function namedEntries(text) {
  return namedPaletteEntries(text).map((e) => {
    const label = (e.role.match(/^(primary|secondary|tertiary)\b/i)?.[1] || (/^accent\b/i.test(e.role) ? "accent1" : "")).toLowerCase();
    let words = `${e.role} ${e.name} ${e.after}`.toLowerCase();
    if (IS_TEXT.test(words)) words = luminance(e.hex) < 0.4 ? "text on light backgrounds" : "text on dark backgrounds";
    return { hex: e.hex, name: e.name, words, label, named: true };
  });
}

const IS_TEXT = /\btext\b|\bcopy\b|\bcaption/;

const RULES = {
  textOnLight: (e) => IS_TEXT.test(e.words) && /light|cream|white/.test(e.words) && !/on dark/.test(e.words),
  textOnDark: (e) => IS_TEXT.test(e.words) && /dark|deep|navy/.test(e.words),
  bgPrimary: (e) => !IS_TEXT.test(e.words) && (/(light|page|cream|white)[\w\s/]{0,12}(base|background)/.test(e.words) || /\bbase\b[\w\s]{0,8}light/.test(e.words)),
  bgDark: (e) => !IS_TEXT.test(e.words) && /(dark|deep)[\w\s/]{0,12}(base|background)/.test(e.words),
  brandAccent: (e) => !IS_TEXT.test(e.words) && (e.label === "accent1" || /\bcta\b|call to action|button/.test(e.words)),
  brandPrimary: (e) => !IS_TEXT.test(e.words) && /(primary|main|dominant|signature)[\w\s]{0,6}brand|brand colou?r/.test(e.words),
};

/**
 * @param palette     the locked "Brand Feeling & Color Palette" prose
 * @param typography  the locked "Typography Pairing" prose
 * @returns { skin: { role: "#HEX" | "Font" }, notes: [sentence], cut }
 */
export function readBrandSkin({ palette = "", typography = "" } = {}) {
  const notes = [];
  const skin = {};
  const { entries, cut, fromNames } = paletteEntries(palette);
  if (cut) notes.push("Your palette text lists more than one palette; the first one was used.");
  if (fromNames && entries.length) notes.push("Your palette names its colors without codes, so Brill matched a code to each name. Set the exact ones in Me → Brand.");
  const used = new Set();
  const take = (role, e, how) => {
    skin[role] = `#${e.hex}`;
    used.add(e.hex);
    if (how) notes.push(how);
  };

  // 1. By the words beside each colour.
  for (const role of ["textOnLight", "textOnDark", "bgPrimary", "bgDark", "brandAccent", "brandPrimary"]) {
    const e = entries.find((x) => !used.has(x.hex) && RULES[role](x));
    if (e) take(role, e);
  }
  const free = () => entries.filter((x) => !used.has(x.hex) && !IS_TEXT.test(x.words));

  // 2. By label and lightness, for what the words did not settle.
  if (!skin.bgPrimary) {
    const e = free().filter((x) => luminance(x.hex) > 0.75).sort((a, b) => luminance(b.hex) - luminance(a.hex))[0];
    if (e) take("bgPrimary", e, `Light background: used ${e.name || `#${e.hex}`}, the lightest color in your palette.`);
  }
  if (!skin.bgDark) {
    const e = free().filter((x) => luminance(x.hex) < 0.1).sort((a, b) => luminance(a.hex) - luminance(b.hex))[0];
    if (e) take("bgDark", e, `Dark background: used ${e.name || `#${e.hex}`}, the darkest color in your palette.`);
  }
  if (!skin.brandPrimary) {
    // The prompt's "Primary" is the page base when it is light; when it is
    // not, it is the dominant brand colour. Otherwise the boldest colour left
    // that is neither near-white nor near-black.
    const labelled = free().find((x) => x.label === "primary");
    const e = labelled || free().filter((x) => x.label !== "accent1" && luminance(x.hex) > 0.03 && luminance(x.hex) < 0.7)
      .sort((a, b) => saturation(b.hex) - saturation(a.hex))[0];
    if (e) take("brandPrimary", e, `Brand color: used ${e.name || `#${e.hex}`}${labelled ? "" : ", the boldest color in your palette"}.`);
  }
  if (!skin.brandAccent) {
    const e = free().find((x) => x.label === "accent2");
    if (e) take("brandAccent", e, `Accent: used ${e.name || `#${e.hex}`}.`);
  }

  if (!entries.length) notes.push(palette ? "No color codes were found in your locked palette." : "My Voice & Branding has no locked palette yet.");
  else {
    const missing = ["bgPrimary", "bgDark", "brandPrimary", "brandAccent"].filter((r) => !skin[r]);
    if (missing.length) notes.push(`Not found in your palette: ${missing.map((r) => ROLE_WORDS[r]).join(", ")}.`);
  }

  // Fonts.
  const fonts = readFonts(typography);
  if (fonts.heading) skin.fontHeading = fonts.heading;
  if (fonts.body) skin.fontBody = fonts.body;
  notes.push(...fonts.notes);

  return { skin, notes, cut };
}

export const ROLE_WORDS = {
  bgPrimary: "a light background", bgDark: "a dark background", brandPrimary: "a brand color",
  brandAccent: "an accent color", textOnLight: "text on light", textOnDark: "text on dark",
  fontHeading: "a heading font", fontBody: "a body font",
};

/** The heading and body fonts from Typography Pairing. */
export function readFonts(text) {
  const notes = [];
  const lines = String(text || "").split(/\r?\n/);
  let heading = null;
  let body = null;
  let mode = null;
  const all = [];
  for (const line of lines) {
    // Labels only — a font NAME can hold a label word ("Playfair Display").
    const l = line.replace(/\{\{[^{}]*\}\}/g, "").toLowerCase();
    if (/heading|headline|display|primary font/.test(l)) mode = "heading";
    else if (/\bbody\b|paragraph|secondary font/.test(l)) mode = "body";
    const names = [...line.matchAll(/\{\{\s*([^{}]+?)\s*\}\}/g)].map((m) => m[1]);
    all.push(...names);
    if (!names.length) continue;
    if (mode === "heading" && !heading) heading = names[0];
    else if (mode === "body" && !body) body = names[0];
  }
  const distinct = [...new Set(all)];
  if (!heading && distinct[0]) { heading = distinct[0]; notes.push(`Heading font: used ${heading}, the first font named.`); }
  if (!body && distinct.find((f) => f !== heading)) { body = distinct.find((f) => f !== heading); notes.push(`Body font: used ${body}, the second font named.`); }
  if (distinct.length > 2) notes.push("Your typography text names more than two fonts; check these are the pair you chose.");
  if (!distinct.length) notes.push(text ? "No fonts were found in your locked typography." : "My Voice & Branding has no locked typography yet.");
  return { heading, body, notes };
}
